import { useState, useEffect } from "react";
import { 
  FaTrash, FaEye, FaSearch, FaTimesCircle, FaMoneyBill, FaUser, FaCalendar, 
  FaTicketAlt, FaGift, FaStar, FaSpinner, FaChevronLeft, FaChevronRight, 
  FaDownload, FaCopy, FaCheck, FaTimes, FaClock, FaCheckCircle, FaHourglassHalf, 
  FaTrophy, FaToggleOn, FaToggleOff, FaUsers, FaAward, FaPlus, FaMinus, 
  FaCrown, FaMedal, FaFilter, FaSort, FaEyeSlash, FaUserCheck,
  FaUserClock, FaUserTag, FaHistory, FaBoxOpen, FaWallet
} from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";

const CampaignBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  
  // Prize Distribution States
  const [isPrizeDistModalOpen, setIsPrizeDistModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignBookings, setCampaignBookings] = useState([]);
  const [winners, setWinners] = useState([]);
  const [prizeAssignments, setPrizeAssignments] = useState([]);
  const [distributing, setDistributing] = useState(false);
  
  // Winners Management States
  const [isWinnersModalOpen, setIsWinnersModalOpen] = useState(false);
  const [selectedCampaignForWinners, setSelectedCampaignForWinners] = useState(null);
  const [campaignWinners, setCampaignWinners] = useState([]);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [winnerFilter, setWinnerFilter] = useState("all"); // all, pending, claimed
  
  // Winner Modal States
  const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
  const [selectedBookingForWinner, setSelectedBookingForWinner] = useState(null);
  const [winningGift, setWinningGift] = useState("");
  const [addingWinner, setAddingWinner] = useState(false);
  
  // Feedback Activation States
  const [activatingFeedback, setActivatingFeedback] = useState(false);
  
  // Ticket Selection States for Prize Distribution
  const [selectedTickets, setSelectedTickets] = useState({}); // { bookingId: [ticketIds] }
  const [showTicketSelector, setShowTicketSelector] = useState({}); // { bookingId: boolean }
  
  const bookingsPerPage = 8; // Reduced from 10 to make table more compact

  useEffect(() => {
    fetchBookings();
  }, []);

  // Get all campaign bookings API
  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get("https://api.dreamtoday.in/api/admin/allcampbookings");
      
      console.log("API Response:", response.data);
      
      if (response.data && response.data.success) {
        setBookings(Array.isArray(response.data.bookings) ? response.data.bookings : []);
        setTotalCount(response.data.totalBookings || 0);
      } else {
        setBookings([]);
        setTotalCount(0);
        setError(response.data?.message || "Failed to fetch campaign bookings");
      }
    } catch (error) {
      console.error("Error fetching campaign bookings:", error);
      setBookings([]);
      setTotalCount(0);
      setError(error.response?.data?.message || "Failed to fetch campaign bookings");
    } finally {
      setLoading(false);
    }
  };

  // Fetch winners for a campaign
  const fetchCampaignWinners = async (campaignId) => {
    try {
      setLoadingWinners(true);
      const response = await axios.get(`https://api.dreamtoday.in/api/admin/campaign-winners/${campaignId}`);
      
      if (response.data && response.data.success) {
        setCampaignWinners(response.data.winners || []);
      }
    } catch (error) {
      console.error("Error fetching campaign winners:", error);
      alert("Failed to fetch winners");
    } finally {
      setLoadingWinners(false);
    }
  };

  // Get unique campaigns from bookings data
  const getUniqueCampaigns = () => {
    const campaignMap = new Map();
    
    bookings.forEach(booking => {
      if (booking.campaignId && !campaignMap.has(booking.campaignId._id)) {
        campaignMap.set(booking.campaignId._id, {
          _id: booking.campaignId._id,
          title: booking.campaignTitle || booking.campaignId.title,
          price: booking.campaignPrice || booking.campaignId.price,
          prizes: booking.prizes || booking.campaignId.prizes || [],
          noOfTickets: booking.campaignId.noOfTickets,
          totalBookings: 0,
          images: booking.campaignImages || booking.campaignId.images || [],
          winners: [] // Will be populated when fetching
        });
      }
    });
    
    // Count bookings per campaign
    bookings.forEach(booking => {
      if (booking.campaignId) {
        const campaign = campaignMap.get(booking.campaignId._id);
        if (campaign) {
          campaign.totalBookings++;
        }
      }
    });
    
    return Array.from(campaignMap.values());
  };

  // Handle campaign selection for prize distribution
  const handleCampaignSelect = (campaignId) => {
    const campaigns = getUniqueCampaigns();
    const campaign = campaigns.find(c => c._id === campaignId);
    setSelectedCampaign(campaign);
    
    // Get bookings for this campaign
    const campaignBookingsList = bookings.filter(
      booking => booking.campaignId && booking.campaignId._id === campaignId
    );
    setCampaignBookings(campaignBookingsList);
    
    // Reset winners and prize assignments
    setWinners([]);
    setPrizeAssignments([]);
    setSelectedTickets({});
    setShowTicketSelector({});
  };

  // Open winners management modal
  const openWinnersModal = (campaign) => {
    setSelectedCampaignForWinners(campaign);
    fetchCampaignWinners(campaign._id);
    setIsWinnersModalOpen(true);
    setWinnerFilter("all");
  };

  // Mark winner as claimed
  const handleMarkWinnerClaimed = async (campaignId, winnerId) => {
    try {
      const response = await axios.put(
        `https://api.dreamtoday.in/api/admin/mark-winner-claimed/${campaignId}/${winnerId}`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        alert("Winner marked as claimed successfully!");
        
        // Update local state
        setCampaignWinners(prev => 
          prev.map(w => 
            w._id === winnerId 
              ? { ...w, status: "claimed", claimedAt: new Date() }
              : w
          )
        );
        
        // Refresh bookings to update winner status in table
        fetchBookings();
      }
    } catch (error) {
      console.error("Error marking winner as claimed:", error);
      alert(error.response?.data?.message || "Failed to mark winner as claimed");
    }
  };

  // Remove winner
  const handleRemoveWinner = async (campaignId, winnerId) => {
    if (!window.confirm("Are you sure you want to remove this winner?")) return;

    try {
      const response = await axios.delete(
        `https://api.dreamtoday.in/api/admin/remove-winner/${campaignId}/${winnerId}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        alert("Winner removed successfully!");
        
        // Update local state
        setCampaignWinners(prev => prev.filter(w => w._id !== winnerId));
        
        // Refresh bookings
        fetchBookings();
      }
    } catch (error) {
      console.error("Error removing winner:", error);
      alert(error.response?.data?.message || "Failed to remove winner");
    }
  };

  // Toggle ticket selector for a booking
  const toggleTicketSelector = (bookingId) => {
    setShowTicketSelector(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  // Handle ticket selection
  const handleTicketSelection = (bookingId, ticketId) => {
    setSelectedTickets(prev => {
      const currentSelected = prev[bookingId] || [];
      
      // If ticket is already selected, remove it
      if (currentSelected.includes(ticketId)) {
        return {
          ...prev,
          [bookingId]: currentSelected.filter(id => id !== ticketId)
        };
      }
      
      // Otherwise add it (only allow one ticket per winner for now)
      return {
        ...prev,
        [bookingId]: [ticketId]
      };
    });
  };

  // Add winner to distribution list with selected ticket
  const addWinner = (booking, prizePosition) => {
    if (winners.some(w => w.bookingId === booking._id)) {
      alert("This user is already selected as a winner");
      return;
    }

    // Check if ticket is selected
    const selectedTicketIds = selectedTickets[booking._id] || [];
    if (selectedTicketIds.length === 0) {
      alert("Please select a winning ticket first");
      return;
    }

    const campaign = selectedCampaign;
    const prize = campaign.prizes.find(p => p.position === prizePosition);
    
    if (!prize) {
      alert("Invalid prize position");
      return;
    }

    // Check if this prize is already assigned
    if (prizeAssignments.some(p => p.prizePosition === prizePosition)) {
      alert(`Prize position ${prizePosition} is already assigned to another winner`);
      return;
    }

    const newWinner = {
      bookingId: booking._id,
      userId: booking.userId,
      userName: booking.userName || booking.userId?.name,
      userEmail: booking.userEmail || booking.userId?.email,
      numberOfTickets: booking.numberOfTickets,
      totalAmount: booking.totalAmount,
      ticketIds: selectedTicketIds, // Use selected tickets instead of all
      prizePosition: prizePosition,
      prizeName: prize.prizeName,
      prizeAmount: prize.prizeAmount || 0,
      prizeType: prize.prizeType || 'product'
    };

    setWinners([...winners, newWinner]);
    setPrizeAssignments([...prizeAssignments, {
      prizePosition: prizePosition,
      prizeName: prize.prizeName,
      bookingId: booking._id
    }]);
    
    // Close ticket selector after selection
    setShowTicketSelector(prev => ({
      ...prev,
      [booking._id]: false
    }));
  };

  // Remove winner from distribution list
  const removeWinner = (bookingId) => {
    const winnerToRemove = winners.find(w => w.bookingId === bookingId);
    if (winnerToRemove) {
      setWinners(winners.filter(w => w.bookingId !== bookingId));
      setPrizeAssignments(prizeAssignments.filter(p => p.bookingId !== bookingId));
      
      // Clear selected tickets for this booking
      setSelectedTickets(prev => {
        const newState = { ...prev };
        delete newState[bookingId];
        return newState;
      });
    }
  };

  // Update prize amount for a winner
  const updateWinnerPrizeAmount = (bookingId, newAmount) => {
    setWinners(prevWinners => 
      prevWinners.map(winner => 
        winner.bookingId === bookingId 
          ? { ...winner, prizeAmount: parseFloat(newAmount) || 0 }
          : winner
      )
    );
  };

  // Distribute prizes to all winners
  const distributePrizes = async () => {
    if (winners.length === 0) {
      alert("Please select at least one winner");
      return;
    }

    try {
      setDistributing(true);
      
      // Create array of winners with their prizes and user details
      const winnersList = winners.map(winner => ({
        bookingId: winner.bookingId,
        userId: winner.userId?._id || winner.userId,
        ticketIds: winner.ticketIds, // Selected ticket IDs
        winningGift: `${winner.prizeName}${winner.prizeAmount > 0 ? ` - ₹${winner.prizeAmount}` : ''}`,
        prizePosition: winner.prizePosition,
        prizeName: winner.prizeName,
        prizeAmount: winner.prizeAmount || 0
      }));

      console.log("Distributing prizes to winners:", winnersList);

      // Call API to distribute prizes
      const response = await axios.post("https://api.dreamtoday.in/api/admin/distribute-prizes", {
        campaignId: selectedCampaign._id,
        campaignTitle: selectedCampaign.title,
        winners: winnersList
      });

      if (response.data && response.data.success) {
        alert(`Prizes distributed successfully to ${response.data.distributedWinners?.length || winners.length} winners!`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          console.warn("Some winners had errors:", response.data.errors);
          alert(`Note: ${response.data.errors.length} winners had errors. Check console for details.`);
        }
        
        // Refresh bookings
        fetchBookings();
        
        // Close modal and reset
        setIsPrizeDistModalOpen(false);
        setSelectedCampaign(null);
        setCampaignBookings([]);
        setWinners([]);
        setPrizeAssignments([]);
        setSelectedTickets({});
        setShowTicketSelector({});
      } else {
        alert(response.data?.message || "Failed to distribute prizes");
      }
    } catch (error) {
      console.error("Error distributing prizes:", error);
      alert(error.response?.data?.message || "Failed to distribute prizes");
    } finally {
      setDistributing(false);
    }
  };

  // Delete booking API
  const handleDelete = async (bookingId) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      try {
        const response = await axios.delete(`https://api.dreamtoday.in/api/admin/deletecampbookings/${bookingId}`);
        
        if (response.data && response.data.success) {
          alert("Booking deleted successfully");
          setBookings(bookings.filter((b) => b._id !== bookingId));
          setTotalCount(prev => prev - 1);
        } else {
          alert(response.data.message || "Failed to delete booking");
        }
      } catch (error) {
        console.error("Error deleting booking:", error);
        alert(error.response?.data?.message || "Failed to delete booking");
      }
    }
  };

  // Add Winner API (Single)
  const handleAddWinner = async () => {
    if (!winningGift.trim()) {
      alert("Please enter a winning gift");
      return;
    }

    try {
      setAddingWinner(true);
      const response = await axios.post(
        `https://api.dreamtoday.in/api/admin/addwinninggift/${selectedBookingForWinner._id}`,
        { winningGift }
      );

      if (response.data && response.data.success) {
        alert("Winner added successfully!");
        
        setBookings(bookings.map(booking => 
          booking._id === selectedBookingForWinner._id 
            ? { ...booking, winningGift } 
            : booking
        ));
        
        setIsWinnerModalOpen(false);
        setSelectedBookingForWinner(null);
        setWinningGift("");
      } else {
        alert(response.data.message || "Failed to add winner");
      }
    } catch (error) {
      console.error("Error adding winner:", error);
      alert(error.response?.data?.message || "Failed to add winner");
    } finally {
      setAddingWinner(false);
    }
  };

  // Activate Feedback API
  const handleActivateFeedback = async (bookingId) => {
    try {
      setActivatingFeedback(true);
      const response = await axios.put(`https://api.dreamtoday.in/api/admin/activate-feedback/${bookingId}`);

      if (response.data && response.data.success) {
        alert("Feedback activated successfully!");
        
        setBookings(bookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, feedbackStatus: "pending" } 
            : booking
        ));
      } else {
        alert(response.data.message || "Failed to activate feedback");
      }
    } catch (error) {
      console.error("Error activating feedback:", error);
      alert(error.response?.data?.message || "Failed to activate feedback");
    } finally {
      setActivatingFeedback(false);
    }
  };

  // Open Winner Modal
  const openWinnerModal = (booking) => {
    setSelectedBookingForWinner(booking);
    setWinningGift(booking.winningGift || "");
    setIsWinnerModalOpen(true);
  };

  // Open Prize Distribution Modal
  const openPrizeDistributionModal = () => {
    setIsPrizeDistModalOpen(true);
  };

  const exportData = (type) => {
    const exportBookings = filteredBookings.map((booking) => ({
      ID: booking?._id || "N/A",
      User: booking?.userName || booking?.userId?.name || "N/A",
      Email: booking?.userEmail || booking?.userId?.email || "N/A",
      Phone: booking?.userId?.phone || "N/A",
      Campaign: booking?.campaignTitle || booking?.campaignId?.title || "N/A",
      Tickets: booking?.numberOfTickets || 0,
      TicketIDs: booking?.ticketIds ? booking.ticketIds.join(', ') : "N/A",
      TotalAmount: booking?.totalAmount || 0,
      Status: booking?.status || booking?.bookingStatus || "N/A",
      Winner: booking?.winningGift || "No",
      WinningGift: booking?.winningGift || "N/A",
      BookingDate: booking?.bookingDate ? new Date(booking.bookingDate).toLocaleString() : "N/A",
      CampaignStart: booking?.campaignStartDate || booking?.campaignId?.startDate ? new Date(booking.campaignStartDate || booking.campaignId?.startDate).toLocaleDateString() : "N/A",
      CampaignEnd: booking?.campaignEndDate || booking?.campaignId?.endDate ? new Date(booking.campaignEndDate || booking.campaignId?.endDate).toLocaleDateString() : "N/A",
      Feedback: booking?.feedback || "N/A",
      Rating: booking?.rating || "N/A",
      FeedbackStatus: booking?.feedbackStatus || "N/A",
    }));
    
    const ws = utils.json_to_sheet(exportBookings);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "CampaignBookings");
    writeFile(wb, `campaign_bookings_${new Date().toISOString().split('T')[0]}.${type}`);
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const copyToClipboard = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedId(text);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const getStatusColor = (status) => {
    const statusStr = status?.toLowerCase() || "";
    
    if (statusStr.includes("booked") || statusStr.includes("active") || statusStr.includes("confirmed")) {
      return "text-green-600 bg-green-50 border border-green-200";
    } else if (statusStr.includes("pending") || statusStr.includes("processing")) {
      return "text-yellow-600 bg-yellow-50 border border-yellow-200";
    } else if (statusStr.includes("cancelled") || statusStr.includes("failed")) {
      return "text-red-600 bg-red-50 border border-red-200";
    } else if (statusStr.includes("completed") || statusStr.includes("won")) {
      return "text-purple-600 bg-purple-50 border border-purple-200";
    } else {
      return "text-gray-600 bg-gray-50 border border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    const statusStr = status?.toLowerCase() || "";
    
    if (statusStr.includes("booked") || statusStr.includes("active") || statusStr.includes("confirmed")) {
      return <FaCheckCircle className="text-green-500" />;
    } else if (statusStr.includes("pending") || statusStr.includes("processing")) {
      return <FaClock className="text-yellow-500" />;
    } else if (statusStr.includes("cancelled") || statusStr.includes("failed")) {
      return <FaTimesCircle className="text-red-500" />;
    } else if (statusStr.includes("completed") || statusStr.includes("won")) {
      return <FaGift className="text-purple-500" />;
    } else {
      return <FaHourglassHalf className="text-gray-500" />;
    }
  };

  const getWinnerStatusBadge = (status) => {
    switch(status) {
      case 'claimed':
        return (
          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <FaCheckCircle className="mr-1" size={10} />
            Claimed
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <FaClock className="mr-1" size={10} />
            Pending
          </span>
        );
    }
  };

  const getFeedbackStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case "submitted":
        return "text-green-600 bg-green-50";
      case "pending":
        return "text-yellow-600 bg-yellow-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const filteredBookings = Array.isArray(bookings) ? bookings.filter((booking) => {
    if (!booking) return false;
    
    const searchTerm = search.toLowerCase();
    const userName = booking?.userName || booking?.userId?.name || "";
    const userEmail = booking?.userEmail || booking?.userId?.email || "";
    const userPhone = booking?.userId?.phone || "";
    const campaignTitle = booking?.campaignTitle || booking?.campaignId?.title || "";
    const winningGift = booking?.winningGift || "";
    const ticketIds = booking?.ticketIds ? booking.ticketIds.join(' ') : "";
    
    return (
      userName.toLowerCase().includes(searchTerm) ||
      userEmail.toLowerCase().includes(searchTerm) ||
      userPhone.toLowerCase().includes(searchTerm) ||
      campaignTitle.toLowerCase().includes(searchTerm) ||
      winningGift.toLowerCase().includes(searchTerm) ||
      ticketIds.toLowerCase().includes(searchTerm) ||
      booking._id?.toLowerCase().includes(searchTerm)
    );
  }) : [];

  const filteredWinners = campaignWinners.filter(winner => {
    if (winnerFilter === "all") return true;
    return winner.status === winnerFilter;
  });

  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking);
  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);

  const calculateStats = () => {
    const validBookings = Array.isArray(bookings) ? bookings.filter(b => b) : [];
    
    const totalRevenue = validBookings.reduce((sum, b) => sum + (b?.totalAmount || 0), 0);
    const totalTickets = validBookings.reduce((sum, b) => sum + (b?.numberOfTickets || 0), 0);
    const activeBookings = validBookings.filter(b => {
      const status = b?.status?.toLowerCase() || b?.bookingStatus?.toLowerCase() || "";
      return status.includes("booked") || status.includes("active") || status.includes("confirmed");
    }).length;
    const feedbackPending = validBookings.filter(b => b?.feedbackStatus?.toLowerCase() === "pending").length;
    const totalWinners = validBookings.filter(b => b?.winningGift).length;
    
    return { totalRevenue, totalTickets, activeBookings, feedbackPending, totalWinners };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto" />
          <p className="mt-4 text-lg text-gray-600">Loading campaign bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl">
          <div className="text-red-500 mb-4">
            <FaTimesCircle className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchBookings}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium shadow-lg hover:shadow-xl"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const uniqueCampaigns = getUniqueCampaigns();

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header with Action Buttons */}
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Campaign Bookings</h1>
            <p className="text-gray-600 text-sm">View and manage all campaign bookings and winners</p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={openPrizeDistributionModal}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-medium shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
            >
              <FaAward />
              <span className="hidden sm:inline">Prize Distribution</span>
            </button>
          </div>
        </div>
        
        {/* Stats Cards - Compact */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <div className="bg-white rounded-lg shadow p-3 border border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaMoneyBill className="text-blue-600 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600 truncate">Revenue</p>
                <p className="text-base font-bold text-gray-900">₹{stats.totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 border border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-lg">

              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600 truncate">Tickets</p>
                <p className="text-base font-bold text-gray-900">{stats.totalTickets.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 border border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FaCheckCircle className="text-purple-600 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600 truncate">Active</p>
                <p className="text-base font-bold text-gray-900">{stats.activeBookings}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-3 border border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FaStar className="text-amber-600 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600 truncate">Feedback</p>
                <p className="text-base font-bold text-gray-900">{stats.feedbackPending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 border border-gray-100 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FaTrophy className="text-yellow-600 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600 truncate">Winners</p>
                <p className="text-base font-bold text-gray-900">{stats.totalWinners}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Export - Compact */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search by name, email, campaign..."
                className="w-full px-3 py-2 pl-9 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => exportData("csv")}
                className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 text-sm font-medium"
                disabled={!Array.isArray(bookings) || bookings.length === 0}
              >
                <FaDownload size={12} />
                <span className="hidden sm:inline">CSV</span>
              </button>
              <button
                onClick={() => exportData("xlsx")}
                className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 text-sm font-medium"
                disabled={!Array.isArray(bookings) || bookings.length === 0}
              >
                <FaDownload size={12} />
                <span className="hidden sm:inline">Excel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Campaigns Quick Access - Compact */}
        {uniqueCampaigns.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <FaUsers className="text-blue-500" />
              Quick Access to Campaign Winners
            </h2>
            <div className="flex flex-wrap gap-2">
              {uniqueCampaigns.slice(0, 5).map(campaign => (
                <button
                  key={campaign._id}
                  onClick={() => openWinnersModal(campaign)}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow transition flex items-center gap-2 text-xs"
                  title={campaign.title}
                >
                  <FaTrophy className="text-yellow-500" size={12} />
                  <span className="max-w-[150px] truncate">{campaign.title}</span>
                </button>
              ))}
              {uniqueCampaigns.length > 5 && (
                <span className="text-xs text-gray-500 self-center">+{uniqueCampaigns.length - 5} more</span>
              )}
            </div>
          </div>
        )}

        {/* Bookings Table - Compact */}
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">User</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">Campaign</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">Booking</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">Tickets</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">Winner</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentBookings.length > 0 ? (
                  currentBookings.map((booking) => (
                    <tr key={booking?._id || Math.random()} className="hover:bg-blue-50/30 transition-colors">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded flex items-center justify-center">
                            <FaUser className="text-white" size={10} />
                          </div>
                          <div className="min-w-0 max-w-[120px]">
                            <div className="text-xs font-medium text-gray-900 truncate" title={booking?.userName || booking?.userId?.name}>
                              {booking?.userName || booking?.userId?.name || "N/A"}
                            </div>
                            <div className="text-[10px] text-gray-500 truncate" title={booking?.userEmail || booking?.userId?.email}>
                              {booking?.userEmail || booking?.userId?.email || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="py-2 px-3">
                        <div className="min-w-0 max-w-[120px]">
                          <div className="text-xs font-medium text-gray-900 truncate" title={booking?.campaignTitle || booking?.campaignId?.title}>
                            {booking?.campaignTitle || booking?.campaignId?.title || "N/A"}
                          </div>
                          {booking?.campaignImages && booking.campaignImages[0] && (
                            <img 
                              src={`https://api.dreamtoday.in${booking.campaignImages[0]}`}
                              alt="Campaign"
                              className="h-5 w-5 rounded object-cover mt-1"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                      </td>
                      
                      <td className="py-2 px-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs">

                            <span className="font-medium">{booking?.numberOfTickets || 0}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <FaMoneyBill className="text-green-500" size={8} />
                            <span className="font-bold text-green-600">₹{booking?.totalAmount || 0}</span>
                          </div>
                        </div>
                      </td>

                      <td className="py-2 px-3">
                        {booking?.ticketIds && booking.ticketIds.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[120px]">
                            {booking.ticketIds.slice(0, 2).map((ticketId, idx) => (
                              <div key={idx} className="flex items-center gap-1 bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[9px]">
                                <span className="font-mono truncate max-w-[50px]" title={ticketId}>
                                  {ticketId.slice(-6)}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(ticketId)}
                                  className="text-gray-400 hover:text-blue-600"
                                >
                                  {copiedId === ticketId ? <FaCheck size={7} className="text-green-500" /> : <FaCopy size={7} />}
                                </button>
                              </div>
                            ))}
                            {booking.ticketIds.length > 2 && (
                              <span className="text-[9px] text-gray-500">+{booking.ticketIds.length - 2}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400">No tickets</span>
                        )}
                      </td>
                      
                      <td className="py-2 px-3">
                        {booking?.winningGift ? (
                          <div className="flex items-center gap-1">
                            <FaTrophy className="text-yellow-500" size={10} />
                            <span className="text-xs font-medium text-yellow-600 truncate max-w-[80px]" title={booking.winningGift}>
                              {booking.winningGift}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                      
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(booking?.status || booking?.bookingStatus)}`}>
                          {getStatusIcon(booking?.status || booking?.bookingStatus)}
                          <span className="ml-1 capitalize truncate max-w-[50px]">
                            {booking?.status || booking?.bookingStatus || "N/A"}
                          </span>
                        </span>
                      </td>
                      
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openBookingDetails(booking)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
                            title="View Details"
                          >
                            <FaEye size={14} />
                          </button>
                          
                          {!booking?.winningGift && (
                            <button
                              onClick={() => openWinnerModal(booking)}
                              className="p-1 text-yellow-600 hover:bg-yellow-50 rounded transition"
                              title="Add Winner"
                            >
                              <FaTrophy size={14} />
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDelete(booking?._id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete Booking"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="py-8 text-center">

                      <p className="text-gray-500 text-sm font-medium">No bookings found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Compact Pagination */}
        {filteredBookings.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-600">
              Showing {indexOfFirstBooking + 1}-{Math.min(indexOfLastBooking, filteredBookings.length)} of {filteredBookings.length}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-xs flex items-center gap-1"
              >
                <FaChevronLeft size={10} /> Prev
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 2) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 1) {
                    pageNumber = totalPages - 2 + i;
                  } else {
                    pageNumber = currentPage - 1 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`w-7 h-7 rounded text-xs font-medium ${
                        currentPage === pageNumber
                          ? "bg-blue-600 text-white"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-xs flex items-center gap-1"
              >
                Next <FaChevronRight size={10} />
              </button>
            </div>
          </div>
        )}

        {/* Prize Distribution Modal - Updated with Ticket Selection */}
        {isPrizeDistModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => {
              setIsPrizeDistModalOpen(false);
              setSelectedCampaign(null);
              setCampaignBookings([]);
              setWinners([]);
              setPrizeAssignments([]);
              setSelectedTickets({});
              setShowTicketSelector({});
            }}></div>
            
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FaAward className="text-3xl" />
                      <div>
                        <h2 className="text-2xl font-bold">Prize Distribution</h2>
                        <p className="text-purple-100">Select winners and their winning tickets</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setIsPrizeDistModalOpen(false);
                        setSelectedCampaign(null);
                        setCampaignBookings([]);
                        setWinners([]);
                        setPrizeAssignments([]);
                        setSelectedTickets({});
                        setShowTicketSelector({});
                      }}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {/* Campaign Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Campaign
                    </label>
                    <select
                      onChange={(e) => handleCampaignSelect(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                      value={selectedCampaign?._id || ""}
                    >
                      <option value="">Choose a campaign...</option>
                      {uniqueCampaigns.map(campaign => (
                        <option key={campaign._id} value={campaign._id}>
                          {campaign.title} - ₹{campaign.price} (Bookings: {campaign.totalBookings})
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedCampaign && (
                    <>
                      {/* Campaign Info with Prizes */}
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <FaMedal className="text-yellow-500" />
                          Available Prizes
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedCampaign.prizes.sort((a, b) => a.position - b.position).map(prize => {
                            const isAssigned = prizeAssignments.some(p => p.prizePosition === prize.position);
                            const prizeTypeIcon = prize.prizeType === 'cash' ? <FaMoneyBill className="text-green-500" /> : <FaBoxOpen className="text-blue-500" />;
                            
                            return (
                              <div key={prize._id} className={`p-3 rounded-lg ${isAssigned ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-200'}`}>
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-gray-500">Position {prize.position}</span>
                                      {prizeTypeIcon}
                                    </div>
                                    <p className="font-medium text-sm">{prize.prizeName}</p>
                                    {prize.prizeAmount > 0 && (
                                      <p className="text-xs text-green-600">₹{prize.prizeAmount}</p>
                                    )}
                                  </div>
                                  {isAssigned && (
                                    <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                                      Assigned
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Bookings List with Ticket Selection */}
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <FaUsers className="text-blue-500" />
                          Users who booked this campaign ({campaignBookings.length})
                        </h3>
                        
                        <div className="space-y-3 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-xl">
                          {campaignBookings.map(booking => {
                            const isWinner = winners.some(w => w.bookingId === booking._id);
                            const winnerDetails = winners.find(w => w.bookingId === booking._id);
                            const selectedTicketIds = selectedTickets[booking._id] || [];
                            const showSelector = showTicketSelector[booking._id];
                            
                            return (
                              <div key={booking._id} className={`p-3 rounded-lg flex flex-col gap-3 ${isWinner ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 text-sm truncate">{booking.userName || booking.userId?.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{booking.userEmail || booking.userId?.email}</p>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                                        {booking.numberOfTickets} tickets
                                      </span>
                                      <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
                                        ₹{booking.totalAmount}
                                      </span>
                                    </div>
                                    {isWinner && winnerDetails && (
                                      <div className="mt-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded inline-flex items-center gap-1">
                                        <FaCrown className="text-yellow-600" size={8} />
                                        Selected: {winnerDetails.prizeName}
                                      </div>
                                    )}
                                    {selectedTicketIds.length > 0 && (
                                      <div className="mt-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1">

                                        Winning Ticket: {selectedTicketIds[0].slice(-6)}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {booking.winningGift ? (
                                    <div className="flex items-center gap-2 shrink-0">
                                      <FaTrophy className="text-yellow-500" size={12} />
                                      <span className="text-xs font-semibold text-yellow-600">Winner</span>
                                    </div>
                                  ) : !isWinner && (
                                    <div className="flex gap-1 flex-wrap shrink-0">
                                      <button
                                        onClick={() => toggleTicketSelector(booking._id)}
                                        className="px-2 py-1 bg-blue-100 text-blue-600 rounded-lg text-[10px] hover:bg-blue-200 transition flex items-center gap-1"
                                      >

                                        {selectedTicketIds.length > 0 ? 'Change Ticket' : 'Select Ticket'}
                                      </button>
                                      {selectedCampaign.prizes
                                        .filter(prize => !prizeAssignments.some(p => p.prizePosition === prize.position))
                                        .slice(0, 2)
                                        .map(prize => (
                                          <button
                                            key={prize._id}
                                            onClick={() => addWinner(booking, prize.position)}
                                            disabled={selectedTicketIds.length === 0}
                                            className={`px-2 py-1 rounded-lg text-[10px] transition flex items-center gap-1 ${
                                              selectedTicketIds.length > 0
                                                ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            }`}
                                            title={selectedTicketIds.length === 0 ? "Select a ticket first" : `Position ${prize.position}`}
                                          >
                                            <FaPlus size={6} />
                                            Pos {prize.position}
                                          </button>
                                        ))}
                                      {selectedCampaign.prizes.filter(prize => !prizeAssignments.some(p => p.prizePosition === prize.position)).length > 2 && (
                                        <span className="text-[10px] text-gray-500 self-center">...</span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Ticket Selector Dropdown */}
                                {showSelector && !isWinner && !booking.winningGift && (
                                  <div className="mt-2 p-3 bg-white border border-blue-200 rounded-lg">
                                    <p className="text-xs font-medium text-gray-700 mb-2">Select Winning Ticket:</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {booking.ticketIds && booking.ticketIds.map(ticketId => (
                                        <label
                                          key={ticketId}
                                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${
                                            selectedTicketIds.includes(ticketId)
                                              ? 'bg-green-50 border-green-300'
                                              : 'bg-gray-50 border-gray-200 hover:bg-blue-50'
                                          }`}
                                        >
                                          <input
                                            type="radio"
                                            name={`ticket-${booking._id}`}
                                            value={ticketId}
                                            checked={selectedTicketIds.includes(ticketId)}
                                            onChange={() => handleTicketSelection(booking._id, ticketId)}
                                            className="hidden"
                                          />
                                          <span className="text-xs font-mono truncate">{ticketId.slice(-8)}</span>
                                        </label>
                                      ))}
                                    </div>
                                    <div className="flex justify-end mt-2">
                                      <button
                                        onClick={() => toggleTicketSelector(booking._id)}
                                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                      >
                                        Done
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Selected Winners Summary */}
                      {winners.length > 0 && (
                        <div className="mb-6">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <FaCrown className="text-yellow-500" />
                            Selected Winners ({winners.length})
                          </h3>
                          
                          <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                            {winners.sort((a, b) => a.prizePosition - b.prizePosition).map(winner => (
                              <div key={winner.bookingId} className="flex justify-between items-center py-2 border-b border-green-100 last:border-0">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm truncate">{winner.userName}</p>
                                  <p className="text-xs text-gray-600">Position {winner.prizePosition}: {winner.prizeName}</p>
                                  <p className="text-xs text-blue-600">Ticket: {winner.ticketIds[0]?.slice(-8)}</p>
                                  
                                  {/* Manual prize amount input - for cash prizes */}
                                  {winner.prizeType === 'cash' && (
                                    <div className="mt-1 flex items-center gap-2">
                                      <label className="text-[10px] text-gray-500">Amount:</label>
                                      <input
                                        type="number"
                                        min="0"
                                        value={winner.prizeAmount}
                                        onChange={(e) => updateWinnerPrizeAmount(winner.bookingId, e.target.value)}
                                        className="w-24 px-2 py-0.5 text-xs border border-green-300 rounded focus:ring-1 focus:ring-green-500"
                                        placeholder="Enter amount"
                                      />
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => removeWinner(winner.bookingId)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                                >
                                  <FaMinus size={10} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {winners.length > 0 && (
                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={distributePrizes}
                            disabled={distributing}
                            className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                          >
                            {distributing ? (
                              <>
                                <FaSpinner className="animate-spin" />
                                Distributing...
                              </>
                            ) : (
                              <>
                                <FaAward />
                                Distribute to {winners.length} Winners
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setIsPrizeDistModalOpen(false);
                              setSelectedCampaign(null);
                              setCampaignBookings([]);
                              setWinners([]);
                              setPrizeAssignments([]);
                              setSelectedTickets({});
                              setShowTicketSelector({});
                            }}
                            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Winners Management Modal */}
        {isWinnersModalOpen && selectedCampaignForWinners && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => {
              setIsWinnersModalOpen(false);
              setSelectedCampaignForWinners(null);
              setCampaignWinners([]);
            }}></div>
            
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3 min-w-0">
                      <FaTrophy className="text-3xl shrink-0" />
                      <div className="min-w-0">
                        <h2 className="text-2xl font-bold truncate">Winners Management</h2>
                        <p className="text-blue-100 truncate">{selectedCampaignForWinners.title}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setIsWinnersModalOpen(false);
                        setSelectedCampaignForWinners(null);
                        setCampaignWinners([]);
                      }}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors shrink-0"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {/* Winners Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-blue-600">Total</p>
                          <p className="text-lg font-bold text-blue-700">{campaignWinners.length}</p>
                        </div>
                        <FaUsers className="text-blue-400 text-lg" />
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-yellow-600">Pending</p>
                          <p className="text-lg font-bold text-yellow-700">
                            {campaignWinners.filter(w => w.status === "pending").length}
                          </p>
                        </div>
                        <FaUserClock className="text-yellow-400 text-lg" />
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-green-600">Claimed</p>
                          <p className="text-lg font-bold text-green-700">
                            {campaignWinners.filter(w => w.status === "claimed").length}
                          </p>
                        </div>
                        <FaUserCheck className="text-green-400 text-lg" />
                      </div>
                    </div>

                    <div className="bg-purple-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-purple-600">Cash</p>
                          <p className="text-lg font-bold text-purple-700">
                            ₹{campaignWinners
                              .filter(w => w.prizeType === 'cash')
                              .reduce((sum, w) => sum + (w.prizeAmount || 0), 0)
                            }
                          </p>
                        </div>
                        <FaWallet className="text-purple-400 text-lg" />
                      </div>
                    </div>
                  </div>

                  {/* Filter Tabs */}
                  <div className="flex gap-2 mb-6">
                    <button
                      onClick={() => setWinnerFilter("all")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        winnerFilter === "all" 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All ({campaignWinners.length})
                    </button>
                    <button
                      onClick={() => setWinnerFilter("pending")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        winnerFilter === "pending" 
                          ? 'bg-yellow-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Pending ({campaignWinners.filter(w => w.status === "pending").length})
                    </button>
                    <button
                      onClick={() => setWinnerFilter("claimed")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        winnerFilter === "claimed" 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Claimed ({campaignWinners.filter(w => w.status === "claimed").length})
                    </button>
                  </div>

                  {/* Winners List - Show Ticket IDs */}
                  {loadingWinners ? (
                    <div className="text-center py-12">
                      <FaSpinner className="animate-spin text-3xl text-blue-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">Loading winners...</p>
                    </div>
                  ) : filteredWinners.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredWinners.sort((a, b) => a.prizePosition - b.prizePosition).map(winner => (
                        <div key={winner._id} className={`p-4 rounded-xl border ${
                          winner.status === 'claimed' 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-yellow-50 border-yellow-200'
                        }`}>
                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <FaCrown className={winner.status === 'claimed' ? 'text-green-600' : 'text-yellow-600'} size={12} />
                                <p className="font-semibold text-gray-900 text-sm truncate">{winner.userName}</p>
                                {getWinnerStatusBadge(winner.status)}
                              </div>
                              
                              <p className="text-xs text-gray-600 truncate mt-1">{winner.userEmail}</p>
                              
                              <div className="flex flex-wrap gap-2 mt-2">
                                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                  {winner.prizeName}
                                </span>
                                {winner.prizeAmount > 0 && (
                                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded">
                                    ₹{winner.prizeAmount}
                                  </span>
                                )}
                              </div>

                              {/* Show Ticket IDs */}
                              {winner.ticketIds && winner.ticketIds.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-[10px] text-gray-500 mb-1">Winning Tickets:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {winner.ticketIds.map((ticketId, idx) => (
                                      <span key={idx} className="text-[8px] bg-white border border-blue-200 text-blue-700 px-1.5 py-0.5 rounded font-mono">
                                        {ticketId.slice(-8)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                                <span>
                                  <FaCalendar className="inline mr-1" size={8} />
                                  {new Date(winner.distributedAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex gap-2 shrink-0">
                              {winner.status === 'pending' && winner.prizeType !== 'cash' && (
                                <button
                                  onClick={() => handleMarkWinnerClaimed(selectedCampaignForWinners._id, winner._id)}
                                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-xs flex items-center gap-1"
                                >
                                  <FaCheck size={10} />
                                  Claim
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveWinner(selectedCampaignForWinners._id, winner._id)}
                                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs flex items-center gap-1"
                              >
                                <FaTrash size={10} />
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FaTrophy className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No winners found for this campaign</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Booking Details Modal - UPDATED ACCORDING TO BACKEND RESPONSE */}
        {isModalOpen && selectedBooking && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => setIsModalOpen(false)}></div>
            
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white z-10 p-6 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="min-w-0">
                      <h2 className="text-2xl font-bold truncate">Booking Details</h2>
                      <p className="text-blue-100 truncate">Complete information about this campaign booking</p>
                    </div>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors shrink-0"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-6">
                    {/* User Information - From userId object */}
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-2 bg-blue-500 rounded-lg mr-3">
                          <FaUser className="text-white" />
                        </div>
                        User Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Name</p>
                          <p className="text-base font-semibold text-gray-900 truncate">
                            {selectedBooking?.userName || selectedBooking?.userId?.name || "N/A"}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-gray-900 break-all text-sm">{selectedBooking?.userEmail || selectedBooking?.userId?.email || "N/A"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-gray-900 text-sm">{selectedBooking?.userId?.phone || selectedBooking?.userPhone || "N/A"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Information - From campaignId object */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-2 bg-green-500 rounded-lg mr-3">
                          <FaGift className="text-white" />
                        </div>
                        Campaign Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-xl shadow-sm md:col-span-2">
                          <p className="text-xs text-gray-500">Campaign Title</p>
                          <p className="text-lg font-bold text-gray-900">
                            {selectedBooking?.campaignTitle || selectedBooking?.campaignId?.title || "N/A"}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Description</p>
                          <p className="text-sm text-gray-700">{selectedBooking?.campaignDescription || selectedBooking?.campaignId?.description || "N/A"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Campaign Price</p>
                          <p className="text-lg font-bold text-green-600">₹{selectedBooking?.campaignPrice || selectedBooking?.campaignId?.price || 0}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Start Date</p>
                          <p className="font-semibold text-sm">
                            {selectedBooking?.campaignStartDate || selectedBooking?.campaignId?.startDate 
                              ? new Date(selectedBooking.campaignStartDate || selectedBooking.campaignId?.startDate).toLocaleDateString() 
                              : "N/A"}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">End Date</p>
                          <p className="font-semibold text-sm">
                            {selectedBooking?.campaignEndDate || selectedBooking?.campaignId?.endDate 
                              ? new Date(selectedBooking.campaignEndDate || selectedBooking.campaignId?.endDate).toLocaleDateString() 
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      
                      {/* Campaign Image */}
                      {selectedBooking?.campaignImages && selectedBooking.campaignImages[0] && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-500 mb-2">Campaign Image</p>
                          <img 
                            src={`https://api.dreamtoday.in${selectedBooking.campaignImages[0]}`}
                            alt="Campaign"
                            className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Booking Information */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-2 bg-purple-500 rounded-lg mr-3">
                          <FaTicketAlt className="text-white" />
                        </div>
                        Booking Information
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Booking ID</p>
                          <p className="text-xs font-mono text-gray-700 break-all">{selectedBooking?._id || "N/A"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Booking Date</p>
                          <p className="text-sm font-semibold">
                            {selectedBooking?.bookingDate ? new Date(selectedBooking.bookingDate).toLocaleString() : "N/A"}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Tickets</p>
                          <p className="text-xl font-bold text-blue-600">{selectedBooking?.numberOfTickets || 0}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="text-xl font-bold text-amber-600">₹{selectedBooking?.totalAmount || 0}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Status</p>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold mt-1 ${getStatusColor(selectedBooking?.status || selectedBooking?.bookingStatus)}`}>
                            {getStatusIcon(selectedBooking?.status || selectedBooking?.bookingStatus)}
                            <span className="ml-1 capitalize">{selectedBooking?.status || selectedBooking?.bookingStatus || "N/A"}</span>
                          </span>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Feedback Status</p>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold mt-1 ${getFeedbackStatusColor(selectedBooking?.feedbackStatus)}`}>
                            {selectedBooking?.feedbackStatus === "pending" ? <FaClock size={10} className="mr-1" /> : <FaCheckCircle size={10} className="mr-1" />}
                            {selectedBooking?.feedbackStatus || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Ticket IDs */}
                      {selectedBooking?.ticketIds && selectedBooking.ticketIds.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            <FaTicketAlt className="text-purple-500" size={12} />
                            Ticket IDs ({selectedBooking.ticketIds.length})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedBooking.ticketIds.map((ticketId, idx) => (
                              <div key={idx} className="flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs border border-purple-200">
                                <span className="font-mono">{ticketId}</span>
                                <button
                                  onClick={() => copyToClipboard(ticketId)}
                                  className="ml-1 text-gray-400 hover:text-purple-600"
                                >
                                  {copiedId === ticketId ? <FaCheck size={8} className="text-green-500" /> : <FaCopy size={8} />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Winning Gift */}
                      {selectedBooking?.winningGift && (
                        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <h4 className="text-sm font-semibold text-yellow-700 mb-2 flex items-center gap-1">
                            <FaTrophy size={12} />
                            Winning Gift
                          </h4>
                          <p className="text-yellow-600 font-medium">{selectedBooking.winningGift}</p>
                        </div>
                      )}
                    </div>

                    {/* Prizes from Campaign */}
                    {selectedBooking?.prizes && selectedBooking.prizes.length > 0 && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <div className="p-2 bg-amber-500 rounded-lg mr-3">
                            <FaMedal className="text-white" />
                          </div>
                          Campaign Prizes
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedBooking.prizes.sort((a, b) => a.position - b.position).map(prize => (
                            <div key={prize._id} className="bg-white p-3 rounded-lg border border-amber-200">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Pos {prize.position}</span>
                                {prize.prizeType === 'cash' ? 
                                  <FaMoneyBill className="text-green-500" size={12} /> : 
                                  <FaBoxOpen className="text-blue-500" size={12} />
                                }
                              </div>
                              <p className="font-medium text-sm mt-1">{prize.prizeName}</p>
                              {prize.prizeAmount > 0 && (
                                <p className="text-xs text-green-600 font-bold">₹{prize.prizeAmount}</p>
                              )}
                              {prize.description && (
                                <p className="text-[10px] text-gray-500 mt-1">{prize.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Winners from this booking - From winners array in booking */}
                    {selectedBooking?.winners && selectedBooking.winners.length > 0 && (
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <div className="p-2 bg-yellow-500 rounded-lg mr-3">
                            <FaCrown className="text-white" />
                          </div>
                          Winners from this Booking ({selectedBooking.winners.length})
                        </h3>
                        <div className="space-y-3">
                          {selectedBooking.winners.map((winner, idx) => (
                            <div key={winner._id || idx} className="bg-white p-4 rounded-lg border border-yellow-200">
                              <div className="flex flex-col md:flex-row justify-between items-start gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <FaCrown className="text-yellow-600" size={14} />
                                    <span className="font-semibold text-gray-900">{winner.userName}</span>
                                    {getWinnerStatusBadge(winner.status)}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">{winner.userEmail}</p>
                                  
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                      {winner.prizeName}
                                    </span>
                                    {winner.prizeAmount > 0 && (
                                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                        ₹{winner.prizeAmount}
                                      </span>
                                    )}
                                    {winner.prizePosition && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                        Position {winner.prizePosition}
                                      </span>
                                    )}
                                  </div>

                                  {/* Ticket IDs for this winner */}
                                  {winner.ticketIds && winner.ticketIds.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-[10px] text-gray-500 mb-1">Winning Tickets:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {winner.ticketIds.map((ticketId, tidx) => (
                                          <div key={tidx} className="flex items-center gap-1 bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded text-[9px] border border-yellow-200">
                                            <span className="font-mono">{ticketId}</span>
                                            <button
                                              onClick={() => copyToClipboard(ticketId)}
                                              className="text-gray-400 hover:text-yellow-600"
                                            >
                                              {copiedId === ticketId ? <FaCheck size={6} className="text-green-500" /> : <FaCopy size={6} />}
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                                    <span>
                                      <FaCalendar className="inline mr-1" size={8} />
                                      Distributed: {new Date(winner.distributedAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="sticky bottom-0 bg-gray-50 border-t p-6">
                  <div className="flex gap-3">
                    {!selectedBooking?.winningGift && (
                      <button
                        onClick={() => {
                          setIsModalOpen(false);
                          openWinnerModal(selectedBooking);
                        }}
                        className="flex-1 py-3 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition font-medium shadow-lg flex items-center justify-center gap-2 text-sm"
                      >
                        <FaTrophy />
                        Add Winner
                      </button>
                    )}
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className={`flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition font-medium shadow-lg text-sm ${
                        selectedBooking?.winningGift ? 'w-full' : ''
                      }`}
                    >
                      Close Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Winner Modal */}
        {isWinnerModalOpen && selectedBookingForWinner && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => {
              setIsWinnerModalOpen(false);
              setSelectedBookingForWinner(null);
              setWinningGift("");
            }}></div>
            
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white p-6 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FaTrophy className="text-2xl" />
                      <h2 className="text-2xl font-bold">Add Winner</h2>
                    </div>
                    <button
                      onClick={() => {
                        setIsWinnerModalOpen(false);
                        setSelectedBookingForWinner(null);
                        setWinningGift("");
                      }}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-2">Selected User:</p>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <p className="font-semibold text-gray-900">{selectedBookingForWinner?.userName || selectedBookingForWinner?.userId?.name}</p>
                      <p className="text-xs text-gray-600 truncate">{selectedBookingForWinner?.userEmail || selectedBookingForWinner?.userId?.email}</p>
                      <p className="text-xs text-gray-600 mt-1">Campaign: {selectedBookingForWinner?.campaignTitle || selectedBookingForWinner?.campaignId?.title}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Winning Gift <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={winningGift}
                      onChange={(e) => setWinningGift(e.target.value)}
                      placeholder="e.g., iPhone 14, Smart TV, etc."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleAddWinner}
                      disabled={addingWinner || !winningGift.trim()}
                      className="flex-1 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-xl hover:from-yellow-600 hover:to-amber-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                    >
                      {addingWinner ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <FaTrophy />
                          Confirm Winner
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setIsWinnerModalOpen(false);
                        setSelectedBookingForWinner(null);
                        setWinningGift("");
                      }}
                      className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignBookings;